import React, { useEffect, useMemo, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./index.css";
import Dropdown from "react-bootstrap/Dropdown";
import { Autoplay, Pagination } from "swiper/modules";

import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Modal,
  Row,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import {
  AreaIcon,
  ArrowLeftIcon,
  CheckmarkBadge04Icon,
  CheckMarkIcon,
  DailerIcon,
  GreenDownloadIcon,
  LocationIcon,
  LocationMapIcon,
  PdfIcon,
  RedUserIcon,
  RupeeIcon,
} from "../../assets/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../context/LoaderContext";
import { formatIndianCompact } from "../../components/common";
import { routes } from "../../routes/pageRoutes";
import BackPageBtnTab from "../../components/back-page-btn-tab";
import {
  changePublicStatus,
  changeStatus,
  clearPropertyDetails,
  fetchPropertyDetails,
} from "../../redux/slice/allPropertiesSlice";
// import { fetchPropertyDetails } from "../../redux/slice/allPropertiesSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import "./index.css";

const ViewNpaPublicProperties = () => {
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const id = params?.id;

  const details = useSelector((state) => state?.allProperties?.propertyDetail);

  const passedPageNo = location?.state?.currentPage;
  // console.log("passedPageNo",passedPageNo);

  //Toast
  const [showToast, setShowToast] = useState(false);
  const [toastVariant, setToastVariant] = useState("toast-success");
  const [toastMessage, setToastMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  useEffect(() => {
    if (id) {
      dispatch(clearPropertyDetails());
      setLoading(true);
      dispatch(fetchPropertyDetails({ id }))
        .then(() => {
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, id, setLoading]);

  const photos = useMemo(
    () => (Array.isArray(details?.photos) ? details.photos : []),
    [details]
  );

  const hasVideo = !!details?.video?.thumbnail_url || !!details?.video?.url;

  const titleText = useMemo(() => {
    if (!details) return "";

    const bhkText = details?.bhk ? `${details.bhk} BHK` : null;
    const sBuiltupArea = details?.s_builtup_area
      ? `${details.s_builtup_area} ${details.s_builtup_area_unit}`
      : null;
    const propertyType = details?.property_type || null;

    const purpose =
      details?.transaction_type?.toLowerCase() === "sell"
        ? "for Sell" // ✅ keep the capitalization as you asked
        : details?.transaction_type
        ? `for ${details.transaction_type}`
        : "";

    const isLand = details?.property_type?.toLowerCase() === "land";

    // ✅ Priority 1: For Land, show land_area, land_area_unit, land_type, and transaction type
    if (isLand) {
      const landArea =
        details?.land_area && details?.land_area_unit
          ? `${details.land_area} ${details.land_area_unit}`
          : details?.land_area;
      const landType = details?.land_type || null;

      if (landArea && landType && purpose) {
        return `${landArea} ${landType} ${purpose}`;
      }
      if (landArea && purpose) {
        return `${landArea} ${purpose}`;
      }

      return `Land ${purpose}`;
    }

    // ✅ Priority 2: "2 BHK, Flat for Sell"
    if (bhkText && propertyType && purpose) {
      return `${bhkText}, ${propertyType} ${purpose}`;
    }

    // ✅ Priority 3: show property_type + purpose
    // if (propertyType && purpose) {
    //   return `${propertyType} ${purpose}`;
    // }

    if (sBuiltupArea && propertyType && purpose) {
      return `${sBuiltupArea} ${propertyType} ${purpose}`;
    }

    // ✅ Priority 4: society name fallback
    if (details?.society_project_name) {
      return `${details.society_project_name} ${purpose}`;
    }

    // ✅ Fallback
    return `Property ${purpose}`.trim();
  }, [details]);

  const totalPrice = useMemo(() => {
    if (details?.final_price) return details.final_price;
    if (details?.price_total) return details.price_total;
    return null;
  }, [details]);

  const unitPriceText = useMemo(() => {
    if (details?.price_per_unit && details?.price_unit) {
      return `${formatIndianCompact(
        Number(details.price_per_unit).toFixed(2)
      )} / ${details.price_unit}`;
    }
    if (details?.price_per_unit) {
      return formatIndianCompact(details.price_per_unit);
    }
    return null;
  }, [details]);

  const builtupText = useMemo(() => {
    if (details?.s_builtup_area) {
      return `${details.s_builtup_area}${
        details?.s_builtup_area_unit ? ` ${details.s_builtup_area_unit}` : ""
      }`;
    }
    if (details?.carpet_area) {
      return `${details.carpet_area}${
        details?.carpet_area_unit ? ` ${details.carpet_area_unit}` : ""
      }`;
    }
    return null;
  }, [details]);

  // const downloadPDF = () => {
  //   const fileUrl = details?.brochure_attachment?.url;  // Use the URL of the file

  //   if (fileUrl) {
  //     // Open the PDF in a new tab
  //     window.open(fileUrl, '_blank');
  //   }
  // };

  const downloadPDF = () => {
    const fileUrl = details?.brochure_attachment?.url; // Use the URL of the file

    if (fileUrl) {
      // Open the PDF in a new tab
      window.open(fileUrl, "_blank");
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Set loading state before the API call
      setLoading(true);

      // API call to update status using Redux dispatch
      const response = await dispatch(
        changeStatus({ id: details.id, status: newStatus })
      ).unwrap();

      if (response.data.success) {
        // On successful status update, show success snackbar
        setToastMessage(`Status updated to ${newStatus}`);
        setToastVariant("toast-success");
        // Fetch updated property details after status change
        await dispatch(fetchPropertyDetails({ id: details.id }));
      } else {
        setToastMessage("Failed to update status");
        setToastVariant("toast-error");
      }
    } catch (error) {
      console.error("Error updating status:", error);

      // Show error snackbar in case of failure
      setToastMessage("Failed to update status");
      setToastVariant("toast-error");
    } finally {
      setLoading(false); // Reset loading state
      setShowToast(true); // Display snackbar
    }
  };

  const handleToggle = () => {
    setModalMessage(
      details?.is_public
        ? "This Property will not be visible to other users once unpublished. Do you want to continue?"
        : "This Property will be visible to all users once published. Do you want to continue?"
    );

    setShowModal(true); // Show the modal to confirm
  };

  const handleConfirm = async (status) => {
    setShowModal(false); // Close the modal
    // setLoading(true);

    try {
      // Dispatch action to change the public status
      const response = await dispatch(
        changePublicStatus({
          id: details.id,
          is_public: status,
        })
      ).unwrap(); // Using unwrap to get the response directly

      if (response.data.success) {
        // Show success message in the toast
        setToastMessage(
          `Status successfully updated to ${
            status ? "Published" : "Unpublished"
          }`
        );
        setToastVariant("toast-success");

        // Fetch updated property details
        await dispatch(fetchPropertyDetails({ id: details.id }));
      } else {
        setToastMessage("Failed to update status");
        setToastVariant("toast-error");
      }
      // Update local state for UI (toggle the status)
    } catch (error) {
      // In case of error, show error toast
      console.error("Error updating status:", error);
      setToastMessage("Failed to update status");
      setToastVariant("toast-error");
      // setLoading(false);
    } finally {
      // Show the toast after the operation
      setShowToast(true);
      // setLoading(false);
    }
  };

  // Back button
  const handleBack = () => {
    navigate(routes.npaPublicProperties);
    sessionStorage.setItem("currentPageNo", passedPageNo);
  };

  const handleVideoPlay = (url) => {
    setVideoUrl(url);
    setShowVideoModal(true);
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setVideoUrl("");
  };

  const isRent = details?.transaction_type?.toLowerCase() === "rent";
  const isMarjo = details?.transaction_type?.toLowerCase() === "marjo";
  const isLand = details?.property_type?.toLowerCase() === "land";

  return (
    <>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={5000}
          autohide
          bg={toastVariant}
          className="position-fixed top-3 end-0 me-3 shadow-none"
        >
          <Toast.Body className="d-flex align-items-center gap-1 lh-sm">
            <CheckmarkBadge04Icon
              width={18}
              height={18}
              color="#28c76f"
              strokeWidth="2"
            />
            <span className="text-capitalize">{toastMessage}</span>
          </Toast.Body>
        </Toast>
      </ToastContainer>
      <Container fluid className="px-0">
        <BackPageBtnTab
          type="button"
          onClick={handleBack}
          icon={
            <ArrowLeftIcon
              width={22}
              height={22}
              color="#444050"
              strokeWidth="2.2"
            />
          }
          title="Details"
        />
        <Row className="mt-4 gy-4 mt-0">
          <Col xl={12} xxl={8}>
            <div className="grid gap-4">
              {(photos.length ||
                hasVideo ||
                titleText ||
                totalPrice ||
                unitPriceText ||
                builtupText) && (
                <Card className="rounded-2 g-col-12 g-col-xl-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                  {/* <Card.Header className="px-0 pt-0 bg-transparent border-0 common-card-header"> */}
                  {/* <div className="d-flex justify-content-between align-items-center gap-3">
                    <h5 className="fw-medium npa-details">
                      {`${details?.assetCategory?.name} for Auction at ${details?.city_town ? details?.city_town : ""}`} 
                    </h5>
                    <StatusChip
                      pill
                      bg={
                        details?.status === "upcoming"
                          ? "chip-warning"
                          : details?.status === "live"
                          ? "chip-green"
                          : details?.status === "expired"
                          ? "chip-red"
                          : "custom-status-chip"
                      }
                      title={details?.status}
                      className="text-capitalize"
                    />
                  </div> */}
                  {/* </Card.Header> */}

                  <Card.Body className="p-0 common-card-body">
                    <div className="grid gap-3">
                      {/* Left Side: Image Slider */}
                      {(photos.length || hasVideo) && (
                        <div
                          className="g-col-12 g-col-md-5"
                          style={{ height: "250px" }}
                        >
                          <div className="property-slider d-flex h-100 overflow-hidden rounded">
                            <Swiper
                              spaceBetween={30}
                              centeredSlides={true}
                              loop={true}
                              pagination={{
                                clickable: true,
                              }}
                              modules={[Autoplay, Pagination]}
                              className="w-100"
                            >
                              {/* Render Photos */}
                              {photos.map((p) => (
                                <SwiperSlide
                                  key={p.id}
                                  className="overflow-hidden d-flex align-items-center justify-content-center h-100"
                                >
                                  <Image
                                    src={p.url}
                                    alt="Property"
                                    className="w-100 h-100 object-fit-cover"
                                  />
                                </SwiperSlide>
                              ))}

                              {/* Render Video */}
                              {hasVideo && (
                                <SwiperSlide
                                  key={details.video.id}
                                  className="overflow-hidden d-flex align-items-center justify-content-center h-100 position-relative"
                                >
                                  <Image
                                    src={details.video.thumbnail_url}
                                    alt="Video Thumbnail"
                                    className="w-100 h-100 object-fit-cover"
                                  />
                                  <div
                                    onClick={() =>
                                      handleVideoPlay(details.video.url)
                                    }
                                    className="play-button position-absolute top-50 start-50 translate-middle"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <svg
                                      width="50"
                                      height="50"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <polygon
                                        points="5,3 19,12 5,21"
                                        fill="white"
                                      />
                                    </svg>
                                  </div>
                                </SwiperSlide>
                              )}
                            </Swiper>
                          </div>
                        </div>
                      )}

                      {/* Right Side: Content */}
                      {(titleText ||
                        totalPrice ||
                        unitPriceText ||
                        builtupText) && (
                        <div
                          className={
                            photos.length || hasVideo
                              ? "g-col-12 g-col-md-7"
                              : "g-col-12"
                          }
                        >
                          {/* Title + Dropdown */}
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-medium m-3">
                              {titleText || "Property"}
                            </h5>
                            {details?.status && (
                              <Dropdown className="">
                                <Dropdown.Toggle
                                  variant="warning"
                                  size="sm"
                                  className="rounded-pill d-flex gap-1 align-items-center px-2"
                                  id="dropdown-basic"
                                >
                                  {details.status}
                                </Dropdown.Toggle>
                                {/* If you want static menu, keep it — value doesn't update backend */}
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => handleStatusChange("Open")}
                                    disabled={details.status === "Open"}
                                  >
                                    Open
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleStatusChange("Close")}
                                    disabled={details.status === "Close"}
                                  >
                                    Close
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </div>

                          {/* Info Blocks */}
                          <Row className="g-3">
                            {totalPrice !== null && (
                              <Col xs={6}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <RupeeIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">
                                      ₹ {formatIndianCompact(totalPrice)}
                                    </h6>
                                    <p className="mb-0 p-light">Total Price</p>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {unitPriceText && (
                              <Col xs={6}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <RupeeIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">
                                      ₹ {unitPriceText}
                                    </h6>
                                    <p className="mb-0 p-light">Unit Price</p>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {/* ✅ RENT blocks (only when transaction_type is Rent) */}
                            {isRent && details?.rent_month && (
                              <Col xs={6}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <RupeeIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">
                                      ₹{" "}
                                      {`${formatIndianCompact(
                                        Number(details.rent_month)
                                      )} / Month`}
                                    </h6>
                                    <p className="mb-0 p-light">
                                      Total Rent / Month
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {isRent && details?.rent_per_unit && (
                              <Col xs={6}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <RupeeIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">
                                      ₹{" "}
                                      {Number(details.rent_per_unit).toFixed(2)}
                                      {/* If you have a unit (e.g. Sqft), append it like this: */}
                                      {details?.price_unit
                                        ? ` / ${details.price_unit}`
                                        : " / Unit"}
                                    </h6>
                                    <p className="mb-0 p-light">Rent / Unit</p>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {/* 🡇 Existing area & BHK blocks stay AFTER rent blocks */}
                            {builtupText && (
                              <Col xs={6}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <AreaIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">
                                      {builtupText}
                                    </h6>
                                    <p className="mb-0 p-light">
                                      S.Builtup Area
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {details?.carpet_area && (
                              <Col xs={6}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <AreaIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">
                                      {`${details.carpet_area} ${
                                        details?.carpet_area_unit ?? ""
                                      }`}
                                    </h6>
                                    <p className="mb-0 p-light">Carpet Area</p>
                                  </div>
                                </div>
                              </Col>
                            )}

                            {details?.bhk && (
                              <Col xs={12}>
                                <div className="info-block d-flex align-items-center mb-2">
                                  <div className="icon-circle me-2">
                                    <AreaIcon />
                                  </div>
                                  <div>
                                    <h6 className="fw-medium mb-0">{`${details.bhk}`}</h6>
                                    <p className="mb-0 p-light">BHK</p>
                                  </div>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </div>
                      )}
                    </div>
                    {/* <div className="d-flex align-items-center gap-2 text-decoration-none mb-2 card-profile">
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-1 user-icon">
                      <span className="text-uppercase fw-normal">
                        {details?.Bank
                          ? BankIcons(details?.Bank?.bank_name)
                          : "NA"}
                      </span>
                    </div>

                    <p className="ms-1 p-light">
                      {details?.Bank ? details?.Bank?.bank_name : "NA"}
                    </p>
                  </div> */}
                    {/* <div className="d-flex flex-column gap-3 card-content">
                    <div>
                      <p className="mb-1 p-light mb-3">
                        Possession Type{" "}
                        <span className="fw-medium">
                          {details?.possessionType
                            ? details?.possessionType?.name
                            : "-"}
                        </span>
                      </p>
                      <p className="mb-3">
                        <StatusChip
                          pill
                          bg="chip-green"
                          title={
                            details?.possessionType
                              ? details?.possessionType?.name
                              : "-"
                          }
                          className="text-capitalize"
                        />
                        <StatusChip
                          pill
                          bg="chip-green"
                          title={
                            details?.propertyType
                              ? details?.propertyType?.name
                              : "-"
                          }
                          className="text-capitalize mx-2"
                        />
                      </p>
                      <h6 className="mb-2 npa-details">About</h6>
                      <p className="mb-1 fw-normal mb-3">
                        {details?.property_address
                          ? details?.property_address
                          : "-"}
                      </p>
                      <div className="d-flex flex-column flex-xxl-row gap-3 w-100">
                        <div className="rounded border p-3 flex-grow-1">
                          <Row>
                            <Col
                              xs={6}
                              className="d-flex flex-column align-items-center border-end"
                            >
                              <p className="mb-1 p-light">Created date</p>
                              <h6 className="npa-details">
                                {details?.created_at
                                  ? moment(details?.created_at).format(
                                      "DD-MM-YYYY"
                                    )
                                  : "-"}
                              </h6>
                            </Col>
                            <Col
                              xs={6}
                              className="d-flex flex-column align-items-center"
                            >
                              <p className="mb-1 p-light">Created time</p>
                              <h6 className="npa-details">
                                {" "}
                                {details?.created_at
                                  ? moment(details?.created_at).format(
                                      "hh:mm A"
                                    )
                                  : "-"}
                              </h6>
                            </Col>
                          </Row>
                        </div>
                        <div className="rounded border p-3 flex-grow-1">
                          {" "}
                          <Row>
                            <Col
                              xs={6}
                              className="d-flex flex-column align-items-center border-end"
                            >
                              <p className="mb-1 p-light">Updated date</p>
                              <h6 className="npa-details">
                                {details?.updated_at
                                  ? moment(details?.updated_at).format(
                                      "DD-MM-YYYY"
                                    )
                                  : "-"}
                              </h6>
                            </Col>
                            <Col
                              xs={6}
                              className="d-flex flex-column align-items-center"
                            >
                              <p className="mb-1 p-light">Updated time</p>
                              <h6 className="npa-details">
                                {details?.updated_at
                                  ? moment(details?.updated_at).format(
                                      "hh:mm A"
                                    )
                                  : "-"}
                              </h6>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div> */}
                  </Card.Body>
                </Card>
              )}

              <Card
                className={`${
                  details?.brochure_attachment?.url
                    ? "g-col-12 g-col-xl-6 g-col-xxl-12"
                    : "g-col-6 g-col-xl-3 g-col-xxl-6"
                } rounded-2 p-3 border-0 common-shadow w-100 common-card`}
              >
                <Card.Header className="px-0 pt-0 pb-2 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <h6 className="mb-2 npa-details">Address</h6>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div className="d-flex justify-content-between align-items-center text-decoration-none mb-3 status-content primary-status">
                    <div className="d-flex align-items-center gap-2">
                      <div className="rounded-2 d-flex align-items-center justify-content-center status-icon">
                        <LocationIcon
                          width={20}
                          height={20}
                          color="#9F2F48"
                          strokeWidth="1.5"
                        />
                      </div>

                      <div className="d-flex flex-column">
                        <p className="mb-0 fw-medium">
                          {isLand
                            ? [
                                details?.village?.name,
                                details?.taluka?.name,
                                details?.district?.name,
                                details?.state?.name,
                              ]
                                .filter(Boolean)
                                .join(", ")
                            : [
                                details?.locality?.name,
                                details?.city?.name,
                                details?.state?.name,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                        </p>
                      </div>
                    </div>

                    <div
                      className="d-flex align-items-center"
                      onClick={() => {
                        const mapsUrl = `https://www.google.com/maps?q=${details?.map_location?.latitude},${details?.map_location?.longitude}`;
                        window.open(mapsUrl, "_blank");
                      }}
                      style={{ cursor: "pointer" }} // Ensure the cursor is a pointer
                    >
                      <LocationMapIcon
                        width={20}
                        height={20}
                        color="#9F2F48"
                        strokeWidth="1.5"
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <h5 className="fw-medium text-capitalize">
                      Auction Details
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div className="grid row-gap-3 column-gap-0 column-gap-xxl-2">
                    <div className="g-col-12 g-col-xl-6 g-col-xxl-3">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Calendar03Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.auction_start_time
                              ? moment(details?.auction_start_time).format(
                                  "DD-MM-YYYY"
                                )
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Auction Start Date
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="g-col-12 g-col-xl-6 g-col-xxl-3">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Clock05Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.auction_start_time
                              ? moment(details?.auction_start_time).format(
                                  "hh:mm A"
                                )
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Auction Start Time
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="g-col-12 g-col-xl-6 g-col-xxl-3">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Calendar03Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.auction_end_time
                              ? moment(details?.auction_end_time).format(
                                  "DD-MM-YYYY"
                                )
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Auction End Date
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="g-col-12 g-col-xl-6 g-col-xxl-3">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Clock05Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.auction_end_time
                              ? moment(details?.auction_end_time).format(
                                  "hh:mm A"
                                )
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Auction End Time
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card> */}
              {(details?.broker?.first_name ||
                details?.broker?.mobile_number) && (
                <Card className="rounded-2 g-col-6 g-col-xl-3 g-col-xxl-6 p-3 border-0 common-shadow w-100 common-card card">
                  <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <h6 className="mb-2 npa-details">Contact Details</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0 common-card-body">
                    <div className="row g-2">
                      <div className="col-12 col-xl-6 col-xxl-6">
                        <div className="d-flex align-items-center status-content primary-status">
                          <div className="rounded-2 d-flex align-items-center justify-content-center status-icon">
                            {/* <span> */}
                            <RedUserIcon
                              width={20}
                              height={20}
                              color="#9F2F48"
                              strokeWidth="1.5"
                            />
                            {/* </span> */}
                          </div>
                          <div>
                            <h6 className="fw-medium">
                              {[
                                details?.broker?.first_name,
                                details?.broker?.last_name,
                              ]
                                .filter(Boolean)
                                .join(" ") || "-"}
                            </h6>
                            <span className="text-capitalize">Name</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-xl-6 col-xxl-6">
                        <div className="d-flex align-items-center status-content primary-status">
                          <div className="rounded-2 d-flex align-items-center justify-content-center status-icon">
                            <DailerIcon
                              width={20}
                              height={20}
                              color="#9F2F48"
                              strokeWidth="1.5"
                              className="detail-icons"
                            />
                          </div>
                          <div>
                            <h6 className="fw-medium">
                              {details?.broker?.mobile_number || "-"}
                            </h6>
                            <span className="text-capitalize">Phone</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {details?.brochure_attachment?.url && (
                <Card className="rounded-2 g-col-6 g-col-xl-3 g-col-xxl-6 p-3 border-0 common-shadow w-100 common-card card">
                  <Card.Header className="px-0 pt-0 bg-transparent border-0 common-card-header">
                    <div>
                      <h5 className="fw-medium text-capitalize mb-1">
                        Brochure Attachment
                      </h5>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4 common-card-body">
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <p className="mb-0 d-flex align-items-center gap-2">
                        <PdfIcon
                          width={22}
                          height={22}
                          color="#7367f0"
                          strokeWidth="2"
                        />
                        {details?.brochure_attachment?.key
                          ? details.brochure_attachment.key.split("/").pop()
                          : "No document available"}
                      </p>
                      <p className="cursor-pointer mb-0" onClick={downloadPDF}>
                        <GreenDownloadIcon
                          width={22}
                          height={22}
                          color="#7367f0"
                          strokeWidth="2"
                        />
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              )}
              {details?.note && (
                <Card className="rounded-2 g-col-12 g-col-xl-6 g-col-xxl-12 p-3 border-0 common-shadow w-100 common-card">
                  <Card.Header className="px-0 pt-0 pb-2 bg-transparent border-0 common-card-header">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <h6 className="mb-2 npa-details">Note</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0 common-card-body">
                    <div className="d-flex align-items-center gap-2 text-decoration-none mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-1 user-icon">
                        <span className="text-uppercase fw-normal">
                          <i className="bi bi-geo-alt"></i>{" "}
                          {/* Icon for address */}
                        </span>
                      </div>

                      <div className="d-flex flex-column">
                        <h6 className="notes">{details.note}</h6>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
              {isMarjo && details?.marjo_note && (
                <Card className="rounded-2 g-col-12 g-col-xl-6 g-col-xxl-12 p-3 border-0 common-shadow w-100 common-card">
                  <Card.Header className="px-0 pt-0 pb-2 bg-transparent border-0 common-card-header">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <h6 className="mb-2 npa-details">Marjo Note</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0 common-card-body">
                    <div className="d-flex align-items-center gap-2 text-decoration-none mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-1 user-icon">
                        <span className="text-uppercase fw-normal">
                          <i className="bi bi-geo-alt"></i>{" "}
                          {/* Icon for address */}
                        </span>
                      </div>

                      <div className="d-flex flex-column">
                        <h6 className="notes">{details.marjo_note}</h6>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* <Card className="rounded-2 g-col-12 p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 bg-transparent border-0 common-card-header">
                  <div>
                    <h5 className="fw-medium text-capitalize mb-1">
                      Downloads
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <p>
                      <PdfIcon
                        width={22}
                        height={22}
                        color="#7367f0"
                        strokeWidth="2"
                      />
                      Property document
                    </p>
                    <p className="cursor-pointer" onClick={downloadPDF}>
                      <GreenDownloadIcon
                        width={22}
                        height={22}
                        color="#7367f0"
                        strokeWidth="2"
                      />
                    </p>
                  </div>
                </Card.Body>
              </Card> */}
            </div>
          </Col>

          <Col xl={12} xxl={4}>
            <div className="grid column-gap-4 column-gap-xxl-0">
              {details?.property_type !== "Land" &&
                (details?.possession ||
                  details?.furnishing_status ||
                  details?.bath ||
                  details?.bhk ||
                  details?.parking_car ||
                  details?.parking_2w ||
                  details?.floor_no ||
                  details?.total_floor ||
                  details?.flat_floor ||
                  details?.society_project_name ||
                  details?.developer_name ||
                  details?.property_type ||
                  details?.transaction_type ||
                  details?.city?.name ||
                  details?.locality?.name ||
                  details?.no_of_wings) && (
                  <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                    <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <h5 className="fw-medium text-capitalize">
                          Property Details
                        </h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0 common-card-body">
                      {details?.possession && (
                        <div className="d-flex align-items-center justify-content-between border-bottom">
                          <p className="p-extra-light mb-2">Possession</p>
                          <p className="p-dark mb-2">{details.possession}</p>
                        </div>
                      )}

                      {details?.furnishing_status && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Furnished</p>
                          <p className="p-dark mb-2">
                            {details.furnishing_status}
                          </p>
                        </div>
                      )}

                      {details?.bath && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Bath Count</p>
                          <p className="p-dark mb-2">{details.bath}</p>
                        </div>
                      )}

                      {details?.bhk && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">BHK</p>
                          <p className="p-dark mb-2">{details.bhk}</p>
                        </div>
                      )}

                      {details?.parking_car && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">
                            Car Parking Available
                          </p>
                          <p className="p-dark mb-2">{details.parking_car}</p>
                        </div>
                      )}

                      {details?.parking_2w && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">
                            2-Wheeler Parking Available
                          </p>
                          <p className="p-dark mb-2">{details.parking_2w}</p>
                        </div>
                      )}

                      {details?.floor_no && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Floor No</p>
                          <p className="p-dark mb-2">{details.floor_no}</p>
                        </div>
                      )}

                      {details?.total_floor && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Total Floor</p>
                          <p className="p-dark mb-2">{details.total_floor}</p>
                        </div>
                      )}

                      {details?.flat_floor && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Flat Floor</p>
                          <p className="p-dark mb-2">{details.flat_floor}</p>
                        </div>
                      )}

                      {details?.society_project_name && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">
                            Society / Project
                          </p>
                          <p className="p-dark mb-2">
                            {details.society_project_name}
                          </p>
                        </div>
                      )}

                      {details?.developer_name && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Developer</p>
                          <p className="p-dark mb-2">
                            {details.developer_name}
                          </p>
                        </div>
                      )}

                      {details?.property_type && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Property Type</p>
                          <p className="p-dark mb-2">{details.property_type}</p>
                        </div>
                      )}

                      {details?.transaction_type && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Transaction Type</p>
                          <p className="p-dark mb-2">
                            {details.transaction_type}
                          </p>
                        </div>
                      )}

                      {details?.city?.name && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">City</p>
                          <p className="p-dark mb-2">{details.city.name}</p>
                        </div>
                      )}

                      {details?.locality?.name && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Locality</p>
                          <p className="p-dark mb-2">{details.locality.name}</p>
                        </div>
                      )}

                      {details?.no_of_wings && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">No. of wings</p>
                          <p className="p-dark mb-2">{details.no_of_wings}</p>
                        </div>
                      )}

                      {details?.lift && (
                        <div className="d-flex align-items-center justify-content-between mt-2">
                          <p className="p-extra-light mb-2">Lift Available</p>
                          <p className="p-dark mb-2">{details.lift}</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

              {isLand &&
                (details?.survey_no ||
                  details?.fp_no ||
                  details?.tp_no ||
                  details?.zone ||
                  details?.jantri_rate ||
                  details?.dastaveg_value ||
                  details?.tenure ||
                  details?.road_type ||
                  details?.road_size ||
                  details?.tags) && (
                  <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                    <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <h5 className="fw-medium text-capitalize">
                          Land Details
                        </h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0 common-card-body">
                      {details?.survey_no && (
                        <div className="d-flex align-items-center justify-content-between border-bottom">
                          <p className="p-extra-light mb-2">Survey No</p>
                          <p className="p-dark mb-2">{details.survey_no}</p>
                        </div>
                      )}

                      {details?.fp_no && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">FP No</p>
                          <p className="p-dark mb-2">{details.fp_no}</p>
                        </div>
                      )}

                      {details?.tp_no && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">TP No</p>
                          <p className="p-dark mb-2">{details.tp_no}</p>
                        </div>
                      )}

                      {details?.zone && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Zone</p>
                          <p className="p-dark mb-2">{details.zone}</p>
                        </div>
                      )}

                      {details?.jantri_rate && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Jantri Rate</p>
                          <p className="p-dark mb-2">
                            {Number(details.jantri_rate).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {details?.dastaveg_value && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Dastaveg Value</p>
                          <p className="p-dark mb-2">
                            {Number(details.dastaveg_value).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {details?.tenure && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Tenure</p>
                          <p className="p-dark mb-2">{details.tenure}</p>
                        </div>
                      )}

                      {details?.road_type && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Road Type</p>
                          <p className="p-dark mb-2">{details.road_type}</p>
                        </div>
                      )}

                      {details?.road_size && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Road Width</p>
                          <p className="p-dark mb-2">{details.road_size}</p>
                        </div>
                      )}

                      {details?.tags && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Tag</p>
                          <p className="p-dark mb-2">{details.tags}</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

              {isRent &&
                (details?.deposit ||
                  details?.maintenance_amount ||
                  details?.municipal_tax ||
                  details?.contract_duration ||
                  details?.rent_increment ||
                  details?.rent_increment_tenure ||
                  details?.notice_period ||
                  details?.lock_in) && (
                  <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                    <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <h5 className="fw-medium text-capitalize">
                          Rental Details
                        </h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0 common-card-body">
                      {details?.deposit && (
                        <div className="d-flex align-items-center justify-content-between border-bottom">
                          <p className="p-extra-light mb-2">Deposit</p>
                          <p className="p-dark mb-2">
                            ₹ {formatIndianCompact(details.deposit)}
                          </p>
                        </div>
                      )}

                      {details?.maintenance_amount && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Maintenance</p>
                          <p className="p-dark mb-2">
                            ₹ {formatIndianCompact(details.maintenance_amount)}
                          </p>
                        </div>
                      )}

                      {details?.municipal_tax && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Municipal Tax</p>
                          <p className="p-dark mb-2">{details.municipal_tax}</p>
                        </div>
                      )}

                      {details?.contract_duration && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">
                            Contract Duration
                          </p>
                          <p className="p-dark mb-2">
                            {details.contract_duration}
                          </p>
                        </div>
                      )}

                      {details?.rent_increment && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Rent Increment</p>
                          <p className="p-dark mb-2">
                            {details.rent_increment}
                          </p>
                        </div>
                      )}

                      {details?.rent_increment_tenure && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">
                            Rent Increment Tenure
                          </p>
                          <p className="p-dark mb-2">
                            {details.rent_increment_tenure}
                          </p>
                        </div>
                      )}

                      {details?.notice_period && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Notice Period</p>
                          <p className="p-dark mb-2">{details.notice_period}</p>
                        </div>
                      )}

                      {details?.lock_in && (
                        <div className="d-flex align-items-center justify-content-between mt-2">
                          <p className="p-extra-light mb-2">Lock-in</p>
                          <p className="p-dark mb-2">{details.lock_in}</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

              {isMarjo &&
                (details?.marjo_property_type ||
                  details?.marjo_city ||
                  details?.marjo_locality ||
                  details?.jantri_rate ||
                  details?.dastaveg_value ||
                  details?.gst ||
                  details?.marjo_price) && (
                  <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                    <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <h5 className="fw-medium text-capitalize">
                          Marjo Details
                        </h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0 common-card-body">
                      {details?.marjo_property_type && (
                        <div className="d-flex align-items-center justify-content-between border-bottom">
                          <p className="p-extra-light mb-2">
                            Type of Property Preferred
                          </p>
                          <p className="p-dark mb-2">
                            {details?.marjo_property_type}
                          </p>
                        </div>
                      )}

                      {details?.marjo_city && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Marjo City</p>
                          <p className="p-dark mb-2">{details.marjo_city}</p>
                        </div>
                      )}

                      {details?.marjo_locality && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Marjo Locality</p>
                          <p className="p-dark mb-2">
                            {details.marjo_locality}
                          </p>
                        </div>
                      )}

                      {details?.jantri_rate && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Jantri Rate</p>
                          <p className="p-dark mb-2">
                            {Number(details.jantri_rate).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {details?.dastaveg_value && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">Dastaveg Value</p>
                          <p className="p-dark mb-2">
                            {Number(details.dastaveg_value).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {details?.gst && (
                        <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                          <p className="p-extra-light mb-2">GST</p>
                          <p className="p-dark mb-2">
                            {Number(details.gst).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {details?.marjo_price && (
                        <div className="d-flex align-items-center justify-content-between mt-2">
                          <p className="p-extra-light mb-2">Marjo Price</p>
                          <p className="p-dark mb-2">
                            {Number(details.marjo_price).toFixed(2)}
                          </p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

              {Array.isArray(details?.amenities) &&
                details.amenities.length > 0 && (
                  <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                    <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <h5 className="fw-medium text-capitalize">Amenities</h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0 common-card-body">
                      <div className="row">
                        {/* Split amenities roughly into two columns without changing CSS */}
                        <div className="col-6">
                          {details.amenities
                            .filter((_, idx) => idx % 2 === 0)
                            .map((am, idx) => (
                              <div
                                className="d-flex gap-1 mb-2"
                                key={`am-l-${idx}`}
                              >
                                <CheckMarkIcon />
                                <p className="p-extra-light mb-0">{am}</p>
                              </div>
                            ))}
                        </div>
                        <div className="col-6">
                          {details.amenities
                            .filter((_, idx) => idx % 2 === 1)
                            .map((am, idx) => (
                              <div
                                className="d-flex gap-1 mb-2"
                                key={`am-r-${idx}`}
                              >
                                <CheckMarkIcon />
                                <p className="p-extra-light mb-0">{am}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

              <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-medium text-capitalize">Published</h5>
                    {/* <Button
                      variant={published ? "danger" : "secondary"}
                      className="rounded-pill px-4"
                      onClick={() => setPublished(!published)}
                    >
                      {published ? "Activated" : "Deactivated"}
                    </Button> */}
                    <Form>
                      <Form.Check
                        type="switch"
                        id="custom-switch"
                        label={details?.is_public ? "Activated" : "Deactivated"}
                        checked={details?.is_public}
                        onChange={handleToggle} // Trigger modal on change
                        custom
                      />
                    </Form>
                  </div>
                </Card.Header>
              </Card>

              {/* <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <h5 className="fw-medium text-capitalize">
                      Property Inspection
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div className="grid row-gap-3 column-gap-0 column-gap-xxl-2">
                    <div className="g-col-12 g-col-xl-6">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Calendar03Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.property_inspection_datetime
                              ? moment(
                                  details?.property_inspection_datetime
                                ).format("DD-MM-YYYY")
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Inspection Date
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="g-col-12 g-col-xl-6">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Clock05Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.property_inspection_datetime
                              ? moment(
                                  details?.property_inspection_datetime
                                ).format("hh:mm A")
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Inspection Time
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card> */}

              {/* <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <h5 className="fw-medium text-capitalize">
                      Application Submission
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div className="grid row-gap-3 column-gap-0 column-gap-xxl-2">
                    <div className="g-col-12 g-col-xl-6">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Calendar03Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.application_submission_date
                              ? moment(
                                  details?.application_submission_date
                                ).format("DD-MM-YYYY")
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Submissions Date
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="g-col-12 g-col-xl-6">
                      <div className="d-flex align-items-center status-content primary-status">
                        <div className="rounded-5 d-flex align-items-center justify-content-center status-icon">
                          <Clock05Icon
                            width={22}
                            height={22}
                            color="#9F2F48"
                            strokeWidth="1.5"
                          />
                        </div>
                        <div>
                          <h6 className="npa-details">
                            {details?.application_submission_date
                              ? moment(
                                  details?.application_submission_date
                                ).format("hh:mm A")
                              : "-"}
                          </h6>
                          <span className="text-capitalize">
                            Submissions Time
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card> */}

              {/* <Card className="rounded-2 g-col-12  p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center gap-3 address-mb">
                    <h5 className="fw-medium text-capitalize">Address</h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14874.616504626927!2d72.87209981497662!3d21.245559737334933!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f4fb5c0b087%3A0xb7aabd8a90da0679!2sMota%20Varachha%2C%20Surat%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1733745823143!5m2!1sen!2sin"
                      width="100%"
                      height="125"
                      title="map"
                      style={{
                        borderRadius: "10px",
                      }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-bottom mb-2 mt-2">
                    <p className="p-extra-light mb-2">Province/State</p>
                    <p className="p-dark mb-2">
                      {details?.province_state ? details?.province_state : "-"}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-bottom mb-2">
                    <p className="p-extra-light mb-2">Taluka</p>
                    <p className="p-dark mb-2">
                      {details?.taluka ? details?.taluka : "-"}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-bottom mb-2">
                    <p className="p-extra-light mb-2">City/Town</p>
                    <p className="p-dark mb-2">
                      {details?.city_town ? details?.city_town : "-"}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <p className="p-extra-light">Area/locality</p>
                    <p className="p-dark">
                      {details?.area_locality ? details?.area_locality : "-"}
                    </p>
                  </div>
                </Card.Body>
              </Card> */}
            </div>
          </Col>
        </Row>
      </Container>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {details?.is_public ? "Unpublish Property" : "Publish Property"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{modalMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            No
          </Button>
          <Button
            variant="primary"
            onClick={() => handleConfirm(!details?.is_public)} // Pass the new status (toggle)
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showVideoModal}
        onHide={handleCloseVideoModal}
        centered
        className="custom-video-modal" // Add a custom class here
      >
        {/* <Modal.Header closeButton>
          <Modal.Title>Watch Video</Modal.Title>
        </Modal.Header> */}
        <Modal.Body>
          <video controls className="w-100" autoPlay>
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ViewNpaPublicProperties;
